/**
 * Business notifications (FNF-415 / FNF-359) — the shapes
 * `/v1/notifications/apps/*` speaks. Mirrors notification-service's
 * `model_notification_preferences.py` and the dashboard mobile app, so the
 * two clients read the same document.
 */

/** The three ways a notification can reach someone. */
export type NotificationChannel = 'push' | 'email' | 'sms';

export const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = ['push', 'email', 'sms'];

/** Column headings over the "What to notify" matrix. */
export const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  push: 'Push',
  email: 'Email',
  sms: 'SMS',
};

/** One channel's own switch, independent of the per-category toggles. */
export interface ChannelPreferences {
  enabled: boolean;
  categories: Record<string, boolean>;
}

/**
 * One row of the matrix. `allowed` says which channels the signed-in role may
 * use for this category (the rest render as a dash); `locked` says which are
 * always on (a lock, not a toggle).
 */
export interface CategoryPreference {
  key: string;
  label: string;
  description: string;
  push: boolean;
  email: boolean;
  sms: boolean;
  allowed: NotificationChannel[];
  locked: NotificationChannel[];
}

/** Times are `HH:mm` (24h) read in `timezone`. */
export interface QuietHours {
  enabled: boolean;
  start: string;
  end: string;
  timezone: string;
}

export type SummaryCadence = 'daily' | 'weekly' | 'off';

/** When the Sales & Reports digest goes out. */
export interface SummarySchedule {
  cadence: SummaryCadence;
  time: string;
}

/** The two numbers the Advanced section edits. */
export interface NotificationThresholds {
  low_rating: number;
  large_order_amount: number | null;
}

/** `GET /v1/notifications/apps/preferences` → `data`. */
export interface NotificationPreferences {
  user_id: string;
  role: string;
  audience: string;
  enabled: boolean;
  channels: Partial<Record<NotificationChannel, boolean>>;
  push?: ChannelPreferences;
  email?: ChannelPreferences;
  sms?: ChannelPreferences;
  categories: CategoryPreference[];
  quiet_hours: QuietHours;
  summary: SummarySchedule;
  thresholds: NotificationThresholds;
  muted_restaurants: string[];
  version: number;
  modified_at?: string;
}

/**
 * The body `PATCH /v1/notifications/apps/preferences` reads. Every field is
 * optional — only what changed is sent.
 */
export interface NotificationPreferencesPatch {
  enabled?: boolean;
  push?: Partial<ChannelPreferences>;
  email?: Partial<ChannelPreferences>;
  sms?: Partial<ChannelPreferences>;
  quiet_hours?: Partial<QuietHours>;
  summary?: Partial<SummarySchedule>;
  thresholds?: {
    low_rating?: number;
    large_order_amount?: number;
    /** The service spells "off" as a flag rather than a null amount. */
    clear_large_order_amount?: boolean;
  };
  muted_restaurants?: string[];
}

/** `metadata` on an inbox row: which event produced it and where it points. */
export interface NotificationMetadata {
  event?: string;
  payload?: Record<string, string>;
}

/** One row of `GET /v1/notifications/apps/messages` → `items[]`. */
export interface InboxNotification {
  notification_id: string;
  user_id?: string;
  title: string;
  body: string;
  notification_type: string;
  is_read: boolean;
  created_at: string;
  metadata?: NotificationMetadata;
}

/** One page of the inbox. */
export interface InboxPage {
  items: InboxNotification[];
  last_evaluated_key: string | null;
  total_count: number;
  unread_count: number;
  read_count?: number;
}

/** `GET /v1/notifications/apps/messages-counts` → what the bell badge reads. */
export interface MessageCounts {
  total_count: number;
  unread_count: number;
}

/**
 * The business categories a dashboard notification belongs to — the filter
 * chips on the inbox and the tag above each row. Mirrors notification-service's
 * `EVENT_CATEGORY` for the business audience.
 */
export interface InboxCategory {
  /** The service's `NotificationCategory` value. */
  key: string;
  label: string;
  /** Boxicons glyph, matching the icon set the rest of the app uses. */
  icon: string;
}

export const INBOX_CATEGORIES: readonly InboxCategory[] = [
  { key: 'PAYMENTS_BILLING', label: 'Payments & billing', icon: 'bx-credit-card' },
  { key: 'PLAN_CHANGES', label: 'Plan changes', icon: 'bx-trending-up' },
  { key: 'ORDER_ALERTS', label: 'Order alerts', icon: 'bx-x-circle' },
  { key: 'ORDER_ACTIVITY', label: 'Order activity', icon: 'bx-receipt' },
  { key: 'SERVICE_REQUESTS', label: 'Table requests', icon: 'bx-bell' },
  { key: 'RESERVATIONS', label: 'Reservations', icon: 'bx-calendar-check' },
  { key: 'REVIEWS', label: 'Reviews', icon: 'bx-star' },
  { key: 'TEAM', label: 'Team', icon: 'bx-group' },
  { key: 'RESTAURANT_STATUS', label: 'Restaurant status', icon: 'bx-store' },
  { key: 'RESTAURANT_DEVICES', label: 'Restaurant & devices', icon: 'bx-devices' },
  { key: 'SALES_REPORTS', label: 'Sales & reports', icon: 'bx-bar-chart-alt-2' },
  { key: 'LOYALTY_PROMOS', label: 'Loyalty & promos', icon: 'bx-award' },
  { key: 'PRODUCT_UPDATES', label: 'Product updates', icon: 'bx-megaphone' },
];

/**
 * Anything the map below does not name — an event newer than this build, or a
 * customer-app event (`NEW_RESTAURANT_NEARBY`) that reached a staff inbox.
 */
export const OTHER_CATEGORY: InboxCategory = {
  key: 'OTHER',
  label: 'Notifications',
  icon: 'bx-bell',
};

const EVENT_CATEGORY: Record<string, string> = {
  DAILY_SUMMARY: 'SALES_REPORTS',
  WEEKLY_SUMMARY: 'SALES_REPORTS',
  SALES_REPORT_READY: 'SALES_REPORTS',
  AI_INSIGHTS_READY: 'SALES_REPORTS',
  PAYMENT_FAILED: 'PAYMENTS_BILLING',
  REFUND_ISSUED: 'PAYMENTS_BILLING',
  SUBSCRIPTION_PAST_DUE: 'PAYMENTS_BILLING',
  SUBSCRIPTION_CANCELLED: 'PAYMENTS_BILLING',
  SUBSCRIPTION_RENEWED: 'PAYMENTS_BILLING',
  TRIAL_ENDING: 'PAYMENTS_BILLING',
  PAYMENT_RECEIVED: 'PAYMENTS_BILLING',
  SUBSCRIPTION_SUSPENDED: 'PAYMENTS_BILLING',
  SUBSCRIPTION_UPGRADED: 'PLAN_CHANGES',
  SUBSCRIPTION_DOWNGRADED: 'PLAN_CHANGES',
  ORDER_CREATED: 'ORDER_ACTIVITY',
  ORDER_SENT_TO_KITCHEN: 'ORDER_ACTIVITY',
  ORDER_READY: 'ORDER_ACTIVITY',
  ORDER_UPDATED: 'ORDER_ACTIVITY',
  ORDER_AUTO_CANCELLED: 'ORDER_ALERTS',
  ORDER_STALE_WARNING: 'ORDER_ALERTS',
  LARGE_ORDER: 'ORDER_ALERTS',
  CALL_WAITER: 'SERVICE_REQUESTS',
  BILL_REQUESTED: 'SERVICE_REQUESTS',
  // A booking request waiting to be accepted or declined (FNF-308).
  RESERVATION_REQUESTED: 'RESERVATIONS',
  NEW_REVIEW: 'REVIEWS',
  LOW_RATING_REVIEW: 'REVIEWS',
  STAFF_INVITED: 'TEAM',
  STAFF_ACTIVATED: 'TEAM',
  STAFF_ROLE_CHANGED: 'TEAM',
  STAFF_SUSPENDED: 'TEAM',
  MENU_PUBLISHED: 'RESTAURANT_DEVICES',
  RESTAURANT_STATUS_CHANGED: 'RESTAURANT_STATUS',
  RESTAURANT_ACTIVATED: 'RESTAURANT_STATUS',
  DEVICE_ENROLLED: 'RESTAURANT_DEVICES',
  DEVICE_UNBOUND: 'RESTAURANT_DEVICES',
  DEVICE_OFFLINE: 'RESTAURANT_DEVICES',
  PROMO_STARTED: 'LOYALTY_PROMOS',
  PROMO_ENDED: 'LOYALTY_PROMOS',
  PEARLS_PAYOUT_SUMMARY: 'LOYALTY_PROMOS',
  FEATURE_ANNOUNCEMENT: 'PRODUCT_UPDATES',
  MAINTENANCE_NOTICE: 'PRODUCT_UPDATES',
};

/**
 * The category of a `metadata.event` value. An unknown event — including a
 * missing one — reads as [OTHER_CATEGORY] rather than throwing: new events
 * ship backend-first and older builds still have to list them.
 */
export function categoryForEvent(event: string | undefined | null): InboxCategory {
  const key = EVENT_CATEGORY[(event ?? '').toUpperCase()];
  return INBOX_CATEGORIES.find((c) => c.key === key) ?? OTHER_CATEGORY;
}

/** Warning, error and alert rows get the attention treatment in the list. */
export function isAlertNotification(notification: InboxNotification): boolean {
  const type = (notification.notification_type ?? '').toLowerCase();
  return type === 'error' || type === 'alert' || type === 'warning';
}

/** Icons for the preference matrix, keyed by the same category values. */
export function categoryIcon(key: string): string {
  return INBOX_CATEGORIES.find((c) => c.key === key.toUpperCase())?.icon ?? OTHER_CATEGORY.icon;
}
